import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import Select from '../components/Select';
import { FormCusto, HistoricoCustos, PainelCustos } from '../components/PainelLavoura';
import Field from '../components/ui/Field';
import EmptyState from '../components/ui/EmptyState';
import Estado from '../components/ui/Estado';
import PageHeader from '../components/ui/PageHeader';
import { toast } from '../components/Toast';
import { descreverArea, unidadeArea } from '../utils/unidades';

// Guarda a última seleção: esta é a tela de uso semanal, e reescolher
// safra e talhão a cada visita seria burocrático.
const LS_SAFRA = 'custos.safraId';
const LS_TALHAO = 'custos.talhaoId';

const ABAS = [
  { id: 'lancar', label: 'Lançar' },
  { id: 'historico', label: 'Histórico' },
  { id: 'custos', label: 'Resumo' },
];

export default function Custos() {
  const [params] = useSearchParams();

  const [safras, setSafras] = useState([]);
  const [safraId, setSafraId] = useState(params.get('safra') || localStorage.getItem(LS_SAFRA) || '');
  const [talhoes, setTalhoes] = useState([]);
  const [talhaoId, setTalhaoId] = useState(params.get('talhao') || localStorage.getItem(LS_TALHAO) || '');
  const [aba, setAba] = useState('lancar');
  const [carregandoSafras, setCarregandoSafras] = useState(true);
  const [erroSafras, setErroSafras] = useState(null);
  const [carregandoTalhoes, setCarregandoTalhoes] = useState(false);
  const [erroTalhoes, setErroTalhoes] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Feedback de ação vai para o toast, canal único do app.
  const showMsg = useCallback((type, text) => {
    toast(text, type === 'error' ? 'error' : 'success');
  }, []);

  const safra = safras.find(s => String(s.id) === String(safraId));
  const talhao = talhoes.find(t => String(t.id) === String(talhaoId));

  const carregarSafras = useCallback(() => {
    setCarregandoSafras(true);
    setErroSafras(null);
    api.getSafras().then(todas => {
      // Safra fechada não aceita lançamento de custo — fica fora do seletor.
      const abertas = (todas || []).filter(s => s.status !== 'FECHADA');
      setSafras(abertas);
      setSafraId(atual => {
        if (abertas.some(s => String(s.id) === String(atual))) return atual;
        return abertas.length > 0 ? String(abertas[abertas.length - 1].id) : '';
      });
    }).catch(() => {
      // Antes o erro era engolido: a tela ficava sem safra selecionada e sem
      // explicação, como se não houvesse safra aberta nenhuma.
      setSafras([]);
      setErroSafras('Não foi possível carregar suas safras. Verifique sua conexão e tente novamente.');
    }).finally(() => setCarregandoSafras(false));
  }, []);

  useEffect(() => { carregarSafras(); }, [carregarSafras]);

  const carregarTalhoes = useCallback(() => {
    if (!safraId) { setTalhoes([]); setErroTalhoes(null); return; }
    setCarregandoTalhoes(true);
    setErroTalhoes(null);
    api.getLavourasAtivasDaSafra(safraId).then(list => {
      const lista = list || [];
      setTalhoes(lista);
      // O talhão guardado pode não pertencer à safra escolhida.
      setTalhaoId(atual => {
        if (lista.some(t => String(t.id) === String(atual))) return atual;
        return lista.length > 0 ? String(lista[0].id) : '';
      });
    }).catch(() => {
      setTalhoes([]);
      setErroTalhoes('Não foi possível carregar as áreas plantadas desta safra. Verifique sua conexão e tente novamente.');
    }).finally(() => setCarregandoTalhoes(false));
  }, [safraId]);

  useEffect(() => { carregarTalhoes(); }, [carregarTalhoes]);

  useEffect(() => {
    if (safraId) localStorage.setItem(LS_SAFRA, safraId);
  }, [safraId]);

  useEffect(() => {
    if (talhaoId) localStorage.setItem(LS_TALHAO, talhaoId);
  }, [talhaoId]);

  // Depois de lançar, histórico e custos precisam ser relidos.
  const aoSalvar = () => setRefreshKey(k => k + 1);

  return (
    <div>
      <PageHeader
        titulo="Manejo"
        icone="manejo"
        descricao="Aplicações e tratos na lavoura"
      />
      <div className="card">
        {/* Seletores: safra e área definem o contexto de tudo abaixo. */}
        <div className="filtros" style={{ marginBottom: '1rem' }}>
          <Field label="Safra / Ciclo" obrigatorio>
            <Select
              value={safraId}
              onChange={setSafraId}
              options={safras.map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} - ${s.descricao}` }))}
              placeholder="Selecione a safra"
            />
          </Field>
          <Field label="Área de cultivo" obrigatorio>
            <Select
              value={talhaoId}
              onChange={setTalhaoId}
              options={talhoes.map(t => ({ value: t.id, label: descreverArea(t.lavoura, t.areaHectares) }))}
              placeholder={talhoes.length === 0 ? 'Nenhuma área plantada' : 'Selecione a área'}
              disabled={talhoes.length === 0}
            />
          </Field>
        </div>

        {/* Carregando, falhou e realmente vazio são três coisas diferentes:
            "Nenhuma safra aberta" só aparece quando a lista chegou vazia. */}
        <Estado
          carregando={carregandoSafras}
          erro={erroSafras}
          aoTentarNovamente={carregarSafras}
          itens={2}
        >
          {!safraId ? (
            safras.length === 0
              ? <EmptyState
                  icone="safra"
                  titulo="Nenhuma safra aberta"
                  descricao="Manejo só pode ser lançado em safras abertas. Crie uma nova safra ou reabra uma existente."
                  acao={{ to: '/safras', label: 'Ir para Safras' }}
                />
              : <EmptyState
                  compacto
                  icone="toque"
                  titulo="Escolha a safra e a área"
                  descricao="Selecione acima onde você quer lançar o manejo."
                />
          ) : (
            <Estado
              carregando={carregandoTalhoes}
              erro={erroTalhoes}
              aoTentarNovamente={carregarTalhoes}
              itens={2}
            >
              {talhoes.length === 0 ? (
                <EmptyState
                  icone="semente"
                  titulo={`Nenhuma área plantada na safra ${safra?.descricao || ''}`}
                  descricao="Antes de lançar o manejo, vincule as áreas de cultivo que você plantou nesta safra."
                  acao={{ to: '/safras', label: 'Vincular áreas' }}
                />
              ) : talhao && (
                <>
            {/* Contexto do talhão: confirma onde o lançamento vai cair. */}
            <div style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                {talhao.lavoura?.tipoArea === 'ESTUFA' ? 'Estufa' : 'Lavoura'} — {talhao.lavoura?.descricao}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                {talhao.tipoGrao}
                {talhao.variedade ? ` · ${talhao.variedade}` : ''}
                {talhao.areaHectares ? ` · ${talhao.areaHectares} ${unidadeArea(talhao.lavoura?.tipoArea)}` : ''}
                {safra?.descricao ? ` · Safra ${safra.descricao}` : ''}
              </div>
            </div>

            {/* Abas no mesmo padrão das outras telas — antes usava um estilo
                próprio com sublinhado, o que dava a impressão de outro app. */}
            <div className="tabs" role="tablist" style={{ marginBottom: '1rem' }}>
              {ABAS.map(a => (
                <button
                  key={a.id}
                  role="tab"
                  aria-selected={aba === a.id}
                  className={`tab ${aba === a.id ? 'active' : ''}`}
                  onClick={() => setAba(a.id)}
                >
                  {a.label}
                </button>
              ))}
            </div>

            {aba === 'lancar' && <FormCusto sl={talhao} onMsg={showMsg} onSalvou={aoSalvar} />}
            {aba === 'historico' && <HistoricoCustos sl={talhao} onMsg={showMsg} onSalvou={aoSalvar} refreshKey={refreshKey} />}
            {aba === 'custos' && <PainelCustos sl={talhao} refreshKey={refreshKey} />}
                </>
              )}
            </Estado>
          )}
        </Estado>
      </div>
    </div>
  );
}
